import React from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import RentalPropertyAnalyzer from './RentalPropertyAnalyzer';
import ScrollToTopOnNavigation from './components/ScrollToTopOnNavigation';
import LoginPage from './components/auth/LoginPage';
import SignupPage from './components/auth/SignupPage';
import ResetPassword from './components/auth/ResetPassword';
import CompletePasswordResetPage from './components/auth/CompletePasswordResetPage';
import AuthActionRedirect from './components/auth/AuthActionRedirect';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import AdminRoleGate from './components/auth/AdminRoleGate';
import ShareViewGate from './components/shared/ShareViewGate';
import ErrorBoundary from './components/ErrorBoundary';
import FeedbackAdminPage from './components/feedback/FeedbackAdminPage';
import ImpersonationBanner from './components/admin/ImpersonationBanner';
import PendingApprovalGate from './components/auth/PendingApprovalGate';
import AccountApprovalsAdminPage from './components/admin/AccountApprovalsAdminPage';
import LegalDocumentPage from './components/legal/LegalDocumentPage';
import AdminLayout from './components/admin/layout/AdminLayout';
import AdminHomePage from './components/admin/AdminHomePage';
import AdminSettingsPage from './components/admin/AdminSettingsPage';
import AdminUsersPage from './components/admin/AdminUsersPage';
import AdminUserDetailPage from './components/admin/AdminUserDetailPage';
import AdminPropertiesPage from './components/admin/AdminPropertiesPage';
import AdminPropertyDetailPage from './components/admin/AdminPropertyDetailPage';
import AdminAuditPage from './components/admin/AdminAuditPage';
import AdminActivityPage from './components/admin/AdminActivityPage';
import AdminProspectsPage from './components/admin/AdminProspectsPage';
import AdminProspectDetailPage from './components/admin/AdminProspectDetailPage';
import AdminProspectPushOpenPage from './components/admin/AdminProspectPushOpenPage';
import AdminProspectFollowUpWorkspacePage from './components/admin/AdminProspectFollowUpWorkspacePage';
import AdminProspectImportPage from './components/admin/AdminProspectImportPage';
import AdminProspectCampaignsPage from './components/admin/AdminProspectCampaignsPage';
import AdminProspectCampaignDetailPage from './components/admin/AdminProspectCampaignDetailPage';
import AdminProspectLetterTemplatesPage from './components/admin/AdminProspectLetterTemplatesPage';
import AdminProspectLetterTemplateNewPage from './components/admin/AdminProspectLetterTemplateNewPage';
import AdminProspectLetterTemplateEditorPage from './components/admin/AdminProspectLetterTemplateEditorPage';
import PublicMailingLandingPage from './components/public/PublicMailingLandingPage';

function App() {
  return (
    <>
      <ScrollToTopOnNavigation />
      <ShareViewGate>
      <AuthActionRedirect />
      <ImpersonationBanner />
      <Routes>
        <Route
          path="/"
          element={
            <ErrorBoundary>
              <ProtectedRoute>
                <PendingApprovalGate>
                  <RentalPropertyAnalyzer />
                </PendingApprovalGate>
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/terms" element={<LegalDocumentPage doc="terms" />} />
        <Route path="/privacy" element={<LegalDocumentPage doc="privacy" />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/complete-password-reset" element={<CompletePasswordResetPage />} />
        <Route path="/p/:token" element={<PublicMailingLandingPage />} />
        <Route
          path="/admin"
          element={
            <ErrorBoundary>
              <ProtectedRoute>
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              </ProtectedRoute>
            </ErrorBoundary>
          }
        >
          <Route index element={<AdminHomePage />} />
          <Route path="feedback" element={<FeedbackAdminPage />} />
          <Route path="approvals" element={<AccountApprovalsAdminPage />} />
          <Route path="account-approvals" element={<Navigate to="/admin/approvals" replace />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:uid" element={<AdminUserDetailPage />} />
          <Route path="properties" element={<AdminPropertiesPage />} />
          <Route path="properties/:propertyId" element={<AdminPropertyDetailPage />} />
          <Route
            path="activity"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminActivityPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="audit"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminAuditPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectsPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/follow-up"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectFollowUpWorkspacePage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/import"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectImportPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/campaigns"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectCampaignsPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/campaigns/:campaignId"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectCampaignDetailPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/letter-templates"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectLetterTemplatesPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/letter-templates/new"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectLetterTemplateNewPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/letter-templates/:templateId"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectLetterTemplateEditorPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/push"
            element={
              <AdminRoleGate allowedRoles={['super_admin']}>
                <AdminProspectPushOpenPage />
              </AdminRoleGate>
            }
          />
          <Route
            path="prospection/:prospectId"
            element={
              <AdminRoleGate allowedRoles={['admin', 'super_admin']}>
                <AdminProspectDetailPage />
              </AdminRoleGate>
            }
          />
        </Route>
      </Routes>
      </ShareViewGate>
    </>
  );
}

export default App;
